import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sparkles, Scroll, Coins, ShieldCheck, ArrowRight, Building2, CheckCircle2, Loader2, Zap,
} from "lucide-react";
import { toast } from "sonner";

const VAULT_BG = "https://static.prod-images.emergentagent.com/jobs/462ba4ae-14ab-4b96-ae38-2885eda5adc2/images/200bde4937c8524c70e7150dc065de9d3464cc12f466d31d3d881ffc08c8faf1.png";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/462ba4ae-14ab-4b96-ae38-2885eda5adc2/images/82a602595c2785525ee8a2daebc230f8872aea58e53b975d2ce436c323afb0c0.png";

function LicenseDialog({ open, onClose, template }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open || !template) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const lic = await api.purchaseLicense({
        buyer_email: email,
        buyer_name: name,
        template_id: template.id,
      });
      toast.success("License reserved", {
        description: `${lic.license_code} · Payment integration coming online`,
      });
      onClose();
      setName("");
      setEmail("");
    } catch (err) {
      toast.error("Could not issue license", { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="license-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-full max-w-md p-8 space-y-5 relative"
      >
        <div>
          <div className="label-tag gold-text">License this Template</div>
          <h3 className="text-2xl font-black tracking-tighter mt-2">{template.name}</h3>
          <p className="text-gray-400 text-sm mt-2">{template.description}</p>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-black gold-text">$49</span>
            <span className="text-xs label-tag">per contract</span>
          </div>
        </div>

        <div className="space-y-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your Name"
            className="w-full bg-black/50 border border-white/10 px-4 py-3 text-sm focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227] rounded-none"
            required
            data-testid="license-name-input"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full bg-black/50 border border-white/10 px-4 py-3 text-sm focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227] rounded-none"
            required
            data-testid="license-email-input"
          />
        </div>

        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="text-xs label-tag hover:text-white" data-testid="license-cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="btn-gold flex-1 inline-flex items-center justify-center gap-2" data-testid="license-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
            Reserve License
          </button>
        </div>

        <p className="text-[10px] text-gray-500 leading-relaxed">
          Stripe payment integration is being wired in. You'll receive a payment link by email within 24h.
        </p>
      </form>
    </div>
  );
}

export default function PublicForge() {
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    api.getTemplates().then(setTemplates).catch(console.error);
    api.forgeStats().then(setStats).catch(console.error);
  }, []);

  const startSubscription = async () => {
    setSubLoading(true);
    try {
      const { checkout_url } = await api.createCheckoutSession({
        lookup_key: "content_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error("Could not start checkout", { description: err.response?.data?.detail || err.message });
      setSubLoading(false);
    }
  };

  return (
    <div className="space-y-20" data-testid="public-forge-page">
      {/* HERO */}
      <section className="relative hero-bg -mx-6 lg:-mx-10 px-6 lg:px-10 py-20 lg:py-28 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        <div className="relative max-w-5xl">
          <div className="inline-flex items-center gap-2 border border-[#C9A227]/40 px-3 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} />
            <span className="text-[10px] label-tag gold-text">The Public Forge · B2B</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            License the
            <br />
            <span className="gold-text">EIGOO Legal Standard</span>.
          </h1>
          <p className="text-gray-300 mt-6 max-w-2xl text-base lg:text-lg leading-relaxed">
            Battle-tested by the Network. Now available to external builders, fleets, and founders.
            One template, one contract, one flat fee.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#templates" className="btn-gold inline-flex items-center gap-2" data-testid="cta-browse-templates">
              Browse the Forge <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://auth.eigooinc.net/login?redirect=https://legal.eigooinc.net/bridge"
              className="btn-ghost-gold inline-flex items-center gap-2"
              data-testid="cta-armada-login"
            >
              Armada Login
            </a>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="trust-stats">
        {[
          { label: "Templates", value: stats?.templates_available ?? "—", icon: Scroll },
          { label: "Network Partners", value: stats?.fleet_admirals ?? "—", icon: ShieldCheck },
          { label: "Contracts Forged", value: stats?.total_contracts ?? "—", icon: Building2 },
          { label: "Licenses Issued", value: stats?.licenses_issued ?? "—", icon: Coins },
        ].map((s) => {
          const I = s.icon;
          return (
            <div key={s.label} className="glass p-6">
              <I className="w-5 h-5 gold-text mb-3" strokeWidth={1.5} />
              <div className="text-3xl font-black tracking-tighter">{s.value}</div>
              <div className="label-tag mt-1">{s.label}</div>
            </div>
          );
        })}
      </section>

      {/* CONTENT SUBSCRIPTION */}
      <section className="card-sharp p-8 md:p-12" data-testid="content-subscription-section">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3 space-y-4">
            <div className="inline-flex items-center gap-2 border border-[#C9A227]/40 px-3 py-1.5">
              <Zap className="w-3.5 h-3.5 gold-text" strokeWidth={1.8} />
              <span className="text-[10px] label-tag gold-text">Content Subscription</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter">
              Everything the Forge produces,<br />
              <span className="gold-text">in one seat.</span>
            </h2>
            <p className="text-gray-300 leading-relaxed max-w-xl">
              Ongoing access to new contract templates, publishing kits, and platform build patterns as
              we ship them. Cancel anytime from your account. No commitments beyond the month you're in.
            </p>
            <ul className="space-y-2 pt-2">
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 className="w-4 h-4 gold-text flex-shrink-0" strokeWidth={2} /> Unlimited access to the full template catalog
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 className="w-4 h-4 gold-text flex-shrink-0" strokeWidth={2} /> Every new template we release, automatically
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 className="w-4 h-4 gold-text flex-shrink-0" strokeWidth={2} /> Signature flow, PDF export, and archives included
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 className="w-4 h-4 gold-text flex-shrink-0" strokeWidth={2} /> Cancel or pause anytime — no annual lock-in
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2 border border-[#C9A227]/40 bg-black/40 p-6 md:p-8">
            <div className="text-center space-y-1">
              <div className="label-tag gold-text">Monthly</div>
              <div className="flex items-baseline justify-center gap-1 mt-2">
                <span className="text-5xl font-black gold-text">$47</span>
                <span className="text-sm text-gray-400">/ month</span>
              </div>
              <div className="text-[10px] label-tag text-gray-500 pt-1">Billed monthly · cancel anytime</div>
            </div>
            <button
              onClick={startSubscription}
              disabled={subLoading}
              className="btn-gold w-full inline-flex items-center justify-center gap-2 mt-6"
              data-testid="content-subscribe-btn"
            >
              {subLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {subLoading ? "Opening checkout…" : "Get Started"}
            </button>
            <p className="text-[10px] text-gray-500 text-center mt-4 leading-relaxed">
              Secure checkout by Stripe. Statement descriptor: <span className="gold-text">EIGOO FORGE</span>. No card details stored on our servers.
            </p>
          </div>
        </div>
      </section>

      {/* TEMPLATES */}
      <section id="templates" className="space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-tag">$49 / contract · royalty-free</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2">The Catalog</h2>
          </div>
          <div className="text-xs text-gray-500 max-w-md">
            Each license unlocks one contract instance. Bulk licenses, white-label, and enterprise plans
            available on request.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="public-templates">
          {templates.map((t, idx) => {
            // Tetris grid: vary spans
            const span = idx % 4 === 0 ? "md:col-span-7" : idx % 4 === 1 ? "md:col-span-5" : idx % 4 === 2 ? "md:col-span-5" : "md:col-span-7";
            return (
              <div
                key={t.id}
                className={`card-sharp p-8 ${span} hover:gold-glow transition-all duration-500 flex flex-col`}
                data-testid={`public-template-${t.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="label-tag gold-text">{t.category}</div>
                    <h3 className="text-2xl font-black tracking-tighter mt-2">{t.name}</h3>
                    <div className="font-mono text-[10px] text-gray-500 mt-1">{t.code}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black gold-text">$49</div>
                    <div className="label-tag">per contract</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-4 leading-relaxed flex-1">{t.description}</p>
                <ul className="mt-5 space-y-2">
                  <li className="flex items-center gap-2 text-xs text-gray-300">
                    <CheckCircle2 className="w-3.5 h-3.5 gold-text" strokeWidth={2} /> Battle-tested by EIGOO Network
                  </li>
                  <li className="flex items-center gap-2 text-xs text-gray-300">
                    <CheckCircle2 className="w-3.5 h-3.5 gold-text" strokeWidth={2} /> Encrypted PDF + signing flow
                  </li>
                  <li className="flex items-center gap-2 text-xs text-gray-300">
                    <CheckCircle2 className="w-3.5 h-3.5 gold-text" strokeWidth={2} /> {t.variable_count} dynamic fields
                  </li>
                </ul>
                <button
                  onClick={() => setSelected(t)}
                  className="btn-gold mt-6 inline-flex items-center justify-center gap-2"
                  data-testid={`license-btn-${t.id}`}
                >
                  License Now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* VAULT FEATURE */}
      <section className="relative card-sharp overflow-hidden" data-testid="vault-feature">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${VAULT_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/40" />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 p-10 lg:p-16">
          <div>
            <div className="label-tag gold-text">Imperial Archives</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 leading-tight">
              Every seal,<br />sealed twice.
            </h2>
            <p className="text-gray-300 mt-5 max-w-md leading-relaxed">
              Each forged contract is encrypted to PDF, mirrored to the GitHub Imperial Vault,
              and indexed for instant retrieval.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 gold-text" /> AES-256 encrypted PDF export</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 gold-text" /> GitHub mirror for tamper-evident log</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 gold-text" /> Tokenized signing — internal or DocuSign</li>
            </ul>
          </div>
        </div>
      </section>

      <LicenseDialog open={!!selected} onClose={() => setSelected(null)} template={selected} />
    </div>
  );
}
